import {
  Alert,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useLanguage } from "../context/LanguageContext";
import {
  WEAR_COST_CUSTOM_REQUIRED_TEXT,
  WEAR_COST_INDICATIVE_UNAVAILABLE_TEXT,
  WEAR_COST_MODE_OPTIONS,
  normalizeBtm,
  wearCostIndicative,
} from "./config/btm";

/**
 * Battery wear priced in the behind-the-meter dispatch.
 *
 * Lives on the BESS page, under Battery Performance, because every input the
 * default price is derived from - the battery's price, its rated cycle life
 * and its state-of-charge window - is set there, and that is where an
 * engineer looks for anything about battery ageing. The value is stored where
 * it always was (btm.wearCost), so saved projects and the run payload are
 * unchanged; the General page now only points here.
 */
export default function BtmWearCostField({ cfg, onChange }) {
  const { language } = useLanguage();
  const isGreek = language === "el";
  const wearCost = normalizeBtm(cfg).wearCost || {};
  const wearMode = WEAR_COST_MODE_OPTIONS.some((o) => o.value === wearCost.mode)
    ? wearCost.mode
    : "auto";
  const description =
    WEAR_COST_MODE_OPTIONS.find((o) => o.value === wearMode)?.description || "";
  const indicative = wearMode === "auto" ? wearCostIndicative(cfg) : null;
  const fmtInt = (v) => Math.round(v).toLocaleString("en-US");
  const patch = (partial) => onChange({ ...wearCost, ...partial });
  const customValue = wearCost.eurPerMwh;
  const customInvalid =
    wearMode === "custom" &&
    (customValue === "" || customValue == null || !(Number(customValue) >= 0));

  return (
    <>
      <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
        <InputLabel>Wear price</InputLabel>
        <Select
          label="Wear price"
          value={wearMode}
          onChange={(e) => patch({ mode: e.target.value })}
        >
          {WEAR_COST_MODE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{description}</FormHelperText>
      </FormControl>
      {wearMode === "auto" && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {indicative
            ? isGreek
              ? `Παραγόμενη τιμή για την επιλεγμένη μπαταρία: ${indicative.eurPerMwh.toFixed(2)} EUR ανά MWh εκφόρτισης — τιμή μπαταρίας EUR ${fmtInt(indicative.fleetPriceEur)} (${indicative.priceBasis === "override" ? "καταχωρημένο κόστος" : "τιμή καταλόγου × πλήθος"}) επί ${fmtInt(indicative.cycles)} εγγυημένων κύκλων × ${fmtInt(indicative.usableKwh)} kWh ωφέλιμων. Ενδεικτική· η έκθεση τυπώνει την τιμή που χρησιμοποίησε η εκτέλεση.`
              : `Derived for the selected battery: ${indicative.eurPerMwh.toFixed(2)} EUR per MWh discharged — battery price EUR ${fmtInt(indicative.fleetPriceEur)} (${indicative.priceBasis === "override" ? "entered cost override" : "catalog price × count"}) over ${fmtInt(indicative.cycles)} warranted cycles × ${fmtInt(indicative.usableKwh)} kWh usable. Indicative; the report prints the price the run used.`
            : WEAR_COST_INDICATIVE_UNAVAILABLE_TEXT}
        </Typography>
      )}
      {wearMode === "custom" && (
        <TextField
          fullWidth
          size="small"
          sx={{ mt: 1.5 }}
          label="Wear price"
          type="number"
          inputProps={{ min: 0, step: "0.1" }}
          value={customValue ?? ""}
          onChange={(e) =>
            patch({ eurPerMwh: e.target.value === "" ? "" : Number(e.target.value) })
          }
          InputProps={{
            endAdornment: <InputAdornment position="end">EUR / MWh discharged</InputAdornment>,
          }}
          error={customInvalid}
          helperText={
            customInvalid
              ? WEAR_COST_CUSTOM_REQUIRED_TEXT
              : "Per MWh the battery discharges. The report prints this figure as the price the dispatch was charged."
          }
        />
      )}
      {wearMode === "off" && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Wear is not priced for this run: the optimiser may cycle the battery for
          any positive gain, however small, up to the daily cycle limit. The report
          says so and shows the cycles actually performed.
        </Alert>
      )}
    </>
  );
}
